import { useParams } from "react-router-dom"
import { useQuery } from "@apollo/client"
import { LoadingIndicatorCentered } from "~/components/LoadingIndicator"
import { CandidateTestLayout } from "~/layouts/CandidateTestLayout"
import { TestQuestionResponsesList } from "~/tests/TestQuestionResponsesList"
import { EmptyData } from "~/components/EmptyData"
import { Error } from "~/ui/Error"
import { shareCandidateTestResponsePath } from "~/common/paths"
import invariant from "tiny-invariant"
import { SHARE_CANDIDATE_TEST_RESPONSES_QUERY_DOCUMENT } from "~/tests/candidateTestQueries"
import { Badge } from "~/shadcn/ui/badge"
import { AlertTriangle } from "lucide-react"
import { useMemo } from "react"

export const ShareCandidateTestScreen = () => {
  const { shareTokenId } = useParams()
  invariant(shareTokenId)

  const { loading, data, error } = useQuery(
    SHARE_CANDIDATE_TEST_RESPONSES_QUERY_DOCUMENT,
    {
      variables: { shareTokenId: shareTokenId },
    }
  )

  const calculateTotalNavigatedAwayCount = useMemo(() => {
    let total = 0

    data?.candidateTestQuestionResponses.nodes.forEach((response) => {
      total += response.navigatedAwayCount
    })
    return total
  }, [data?.candidateTestQuestionResponses.nodes])

  if (loading) {
    return <LoadingIndicatorCentered />
  }

  if (error || !data?.candidateTest) {
    console.error("ERROR", error)
    return <Error message="Error loading candidate test token" />
  }

  return (
    <CandidateTestLayout
      test={data.candidateTest.test}
      candidateTest={data.candidateTest}
      backLabel="Responses"
      backPath={""}
      navigation={<></>}
      overProfile={
        <>
          {calculateTotalNavigatedAwayCount > 0 && (
            <div className="flex items-center px-3 py-2 rounded text-sm border border-yellow-dark bg-yellow-light text-gray-2d2d2d">
              <AlertTriangle
                size={16}
                className="inline mr-3 text-yellow-dark"
              />
              <span className="text-sm">
                During the test this candidate navigated away from the test
                {" - "}
                <span className="font-semibold">
                  {calculateTotalNavigatedAwayCount} times
                </span>
              </span>
            </div>
          )}
        </>
      }
    >
      {data && data.candidateTestQuestionResponses.nodes.length > 0 ? (
        <>
          {(data.candidateTest.aiBio || data.candidateTest.aiSummary) && (
            <>
              <div className="flex flex-col gap-4 p-10 mb-10 border border-gray-E6E6E3 rounded-lg max-h-[300px] overflow-y-auto">
                {data.candidateTest.aiBio && (
                  <div className="flex flex-col gap-2">
                    <div className="text-gray-999 text-lg font-medium">
                      Candidate Bio
                      <Badge className="ml-2" variant="default">
                        Beta
                      </Badge>
                    </div>
                    <div className="text-gray-333 text-base leading-7">
                      {data.candidateTest.aiBio}
                    </div>
                  </div>
                )}
                {data.candidateTest.aiSummary && (
                  <div className="flex flex-col gap-2">
                    <div className="text-gray-999 text-lg font-medium">
                      Test Summary
                      <Badge className="ml-2" variant="default">
                        Beta
                      </Badge>
                    </div>
                    <div className="text-gray-333 text-base leading-7">
                      {data.candidateTest.aiSummary}
                    </div>
                  </div>
                )}
              </div>
              <h3 className="text-2xl">Candidate Responses</h3>
            </>
          )}
          <TestQuestionResponsesList
            data={data.candidateTestQuestionResponses.nodes}
            viewPath={(response) =>
              shareCandidateTestResponsePath({
                shareTokenId,
                questionResponseId: response.id,
              })
            }
            canEdit={false}
            onlyAnswered
          />
        </>
      ) : (
        <EmptyData>No responses yet.</EmptyData>
      )}
    </CandidateTestLayout>
  )
}
